import { FlightPaymentType } from '@customTypes/types';
import {
  Flex,
  List,
  ListItem,
  RadioGroup,
  Space,
  Stack,
  Text,
  Title
} from '@mantine/core';

import { PageLoading } from '@/pages/PageLoading';
import { useContractTermsPage } from '@/pages/club/onboard/components/stages/contract-terms/hooks/useContractTermsPage';
import { translate } from '@/shared/i18n/translate';
import {
  LBButton,
  LBCheckbox,
  LBNumberInput,
  LBRadio,
  LBTextInput
} from '@/shared/ui-kit/components';

export const ContractTerms = () => {
  const {
    state: { form, isLoading },
    functions: { onSubmit }
  } = useContractTermsPage();

  if (isLoading) return <PageLoading />;

  return (
    <form onSubmit={onSubmit}>
      <Title order={1}>
        {translate('pages.onboarding.club.contractTerms.title')}
      </Title>
      <Space h={32} />
      <Stack gap={24}>
        <Flex direction='column' gap={8}>
          <Text>
            {translate(
              'pages.onboarding.club.contractTerms.form.minimalContractTerm.label'
            )}
          </Text>
          <Flex align='start' justify='space-between'>
            <Text>
              {translate(
                'pages.onboarding.club.contractTerms.form.minimalContractTerm.unit'
              )}
            </Text>
            <LBNumberInput
              key={form.key('data.minContractDurationWeeks')}
              disabled={form.submitting}
              maw={150}
              placeholder='0'
              {...form.getInputProps('data.minContractDurationWeeks')}
            />
          </Flex>
        </Flex>
        <Flex direction='column' gap={8}>
          <Text>
            {translate(
              'pages.onboarding.club.contractTerms.form.artistsSchedule.label'
            )}
          </Text>
          <RadioGroup
            value={
              form.getValues().additional.artistWorkSchedule === ''
                ? 'other'
                : form.getValues().additional.artistWorkSchedule
            }
            onChange={(value) => {
              if (value === 'other') {
                form.setFieldValue('additional.artistWorkSchedule', value);
                form.setFieldValue('data.artistWorkSchedule', '4/2');

                return;
              }
              form.setFieldValue('additional.artistWorkSchedule', value);
            }}
          >
            <Stack gap={8}>
              <LBRadio disabled={form.submitting} label='5/2' value='5/2' />
              <LBRadio disabled={form.submitting} label='6/1' value='6/1' />
              <Flex align='center' justify='space-between'>
                <LBRadio
                  disabled={form.submitting}
                  label={translate('commons.other')}
                  value='other'
                />
                <LBTextInput
                  key={form.key('data.artistWorkSchedule')}
                  maw={124}
                  placeholder='4/2'
                  disabled={
                    form.getValues().additional.artistWorkSchedule === '5/2' ||
                    form.getValues().additional.artistWorkSchedule === '6/1' ||
                    form.submitting
                  }
                  {...form.getInputProps('data.artistWorkSchedule')}
                  // value={
                  //   form.getValues().data.artistWorkSchedule !== '5/2' &&
                  //   form.getValues().data.artistWorkSchedule !== '6/1'
                  //     ? form.getValues().data.artistWorkSchedule
                  //     : ''
                  // }
                  // onChange={(e) => {
                  //   form.setFieldValue(
                  //     'data.artistWorkSchedule',
                  //     e.target.value
                  //   );
                  // }}
                />
              </Flex>
            </Stack>
          </RadioGroup>
        </Flex>
        <Flex direction='column' gap={8}>
          <Text>{translate('commons.flightTickets')}</Text>
          <RadioGroup
            defaultValue={FlightPaymentType.Club}
            value={form.getValues().data.flightPaymentType}
            onChange={(value) => {
              if (
                (value as FlightPaymentType) !== FlightPaymentType.Compensation
              ) {
                form.setFieldValue('additional.compensationForOneMonth', false);
                form.setFieldValue(
                  'additional.compensationForTwoMonths',
                  false
                );
                form.setFieldValue(
                  'additional.compensationForThreeMonths',
                  false
                );
                form.setFieldValue(
                  'data.freeReturnTicketAfterSixMonths',
                  false
                );
                form.clearFieldError('data.compensationForOneMonth');
                form.clearFieldError('data.compensationForTwoMonths');
                form.clearFieldError('data.compensationForThreeMonths');
              }
              form.setFieldValue(
                'data.flightPaymentType',
                value as FlightPaymentType
              );
            }}
          >
            <Stack gap={8}>
              <LBRadio
                disabled={form.submitting}
                label={translate('commons.flightPaymentType.Club')}
                value={FlightPaymentType.Club}
              />
              <LBRadio
                disabled={form.submitting}
                label={translate('commons.flightPaymentType.Artist')}
                value={FlightPaymentType.Artis}
              />
              <LBRadio
                disabled={form.submitting}
                label={translate('commons.flightPaymentType.Credit')}
                value={FlightPaymentType.Credit}
              />
              <LBRadio
                disabled={form.submitting}
                label={translate('commons.flightPaymentType.Agreement')}
                value={FlightPaymentType.Agreement}
              />
              <LBRadio
                disabled={form.submitting}
                label={translate('commons.flightPaymentType.Compensation')}
                value={FlightPaymentType.Compensation}
              />
              <List withPadding listStyleType='none'>
                <Stack gap={16}>
                  <ListItem>
                    <Flex direction='column' gap={8}>
                      <LBCheckbox
                        checked={
                          form.getValues().additional.compensationForOneMonth
                        }
                        disabled={
                          form.getValues().data.flightPaymentType !==
                            FlightPaymentType.Compensation || form.submitting
                        }
                        label={translate(
                          'commons.flightCompensationOptions.oneMonth'
                        )}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            form.setFieldValue(
                              'additional.compensationForOneMonth',
                              e.currentTarget.checked
                            );
                          }
                          form.setFieldValue(
                            'additional.compensationForOneMonth',
                            e.currentTarget.checked
                          );
                          form.setFieldValue('data.compensationForOneMonth', 0);
                          form.clearFieldError('data.compensationForOneMonth');
                        }}
                      />
                      <List withPadding listStyleType='none'>
                        <ListItem>
                          <LBNumberInput
                            key={form.key('data.compensationForOneMonth')}
                            maw={124}
                            placeholder='0'
                            rightSection={<Text>%</Text>}
                            disabled={
                              !form.getValues().additional
                                .compensationForOneMonth || form.submitting
                            }
                            {...form.getInputProps(
                              'data.compensationForOneMonth'
                            )}
                          />
                        </ListItem>
                      </List>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex direction='column' gap={8}>
                      <LBCheckbox
                        checked={
                          form.getValues().additional.compensationForTwoMonths
                        }
                        disabled={
                          form.getValues().data.flightPaymentType !==
                            FlightPaymentType.Compensation || form.submitting
                        }
                        label={translate(
                          'commons.flightCompensationOptions.twoMonths'
                        )}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            form.setFieldValue(
                              'additional.compensationForTwoMonths',
                              e.currentTarget.checked
                            );
                          }
                          form.setFieldValue(
                            'additional.compensationForTwoMonths',
                            e.currentTarget.checked
                          );
                          form.setFieldValue(
                            'data.compensationForTwoMonths',
                            0
                          );
                          form.clearFieldError('data.compensationForTwoMonths');
                        }}
                      />
                      <List withPadding listStyleType='none'>
                        <ListItem>
                          <LBNumberInput
                            key={form.key('data.compensationForTwoMonths')}
                            maw={124}
                            placeholder='0'
                            rightSection={<Text>%</Text>}
                            disabled={
                              !form.getValues().additional
                                .compensationForTwoMonths || form.submitting
                            }
                            {...form.getInputProps(
                              'data.compensationForTwoMonths'
                            )}
                          />
                        </ListItem>
                      </List>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex direction='column' gap={8}>
                      <LBCheckbox
                        checked={
                          form.getValues().additional.compensationForThreeMonths
                        }
                        disabled={
                          form.getValues().data.flightPaymentType !==
                            FlightPaymentType.Compensation || form.submitting
                        }
                        label={translate(
                          'commons.flightCompensationOptions.threeMonths'
                        )}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            form.setFieldValue(
                              'additional.compensationForThreeMonths',
                              e.currentTarget.checked
                            );
                          }
                          form.setFieldValue(
                            'additional.compensationForThreeMonths',
                            e.currentTarget.checked
                          );
                          form.setFieldValue(
                            'data.compensationForThreeMonths',
                            0
                          );
                          form.clearFieldError(
                            'data.compensationForThreeMonths'
                          );
                        }}
                      />
                      <List withPadding listStyleType='none'>
                        <ListItem>
                          <LBNumberInput
                            key={form.key('data.compensationForThreeMonths')}
                            maw={124}
                            placeholder='0'
                            rightSection={<Text>%</Text>}
                            disabled={
                              !form.getValues().additional
                                .compensationForThreeMonths || form.submitting
                            }
                            {...form.getInputProps(
                              'data.compensationForThreeMonths'
                            )}
                          />
                        </ListItem>
                      </List>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <LBCheckbox
                      key={form.key('data.freeReturnTicketAfterSixMonths')}
                      disabled={
                        form.getValues().data.flightPaymentType !==
                          FlightPaymentType.Compensation || form.submitting
                      }
                      label={translate(
                        'commons.flightCompensationOptions.sixMonths'
                      )}
                      {...form.getInputProps(
                        'data.freeReturnTicketAfterSixMonths',
                        { type: 'checkbox' }
                      )}
                    />
                  </ListItem>
                </Stack>
              </List>
            </Stack>
          </RadioGroup>
        </Flex>
        <Flex direction='column' gap={8}>
          <Text>{translate('commons.transportFromAirport.label')}</Text>
          <RadioGroup
            value={JSON.stringify(
              form.getValues().data.isTransferOrganizedByClub
            )}
            onChange={(value) => {
              form.setFieldValue(
                'data.isTransferOrganizedByClub',
                JSON.parse(value) as boolean
              );
            }}
          >
            <Stack gap={8}>
              <LBRadio
                disabled={form.submitting}
                value='true'
                label={translate(
                  'commons.transportFromAirport.options.clubOrganizes'
                )}
              />
              <LBRadio
                disabled={form.submitting}
                value='false'
                label={translate(
                  'commons.transportFromAirport.options.artistSelf'
                )}
              />
            </Stack>
          </RadioGroup>
        </Flex>
      </Stack>
      <Space h={32} />
      <LBButton
        fullWidth
        disabled={!form.isValid()}
        loading={form.submitting}
        type='submit'
      >
        {translate('commons.save-and-continue')}
      </LBButton>
    </form>
  );
};
